import { CITATION } from "./selectors";

export const parseAuthors = (contributors = []) => {
  return contributors.reduce((authors, { name, type }) => {
    if ((!type && !!name) || type === "author") {
      authors.push(parseAuthorsName(name));
    }
    return authors;
  }, []);
};

const parseAuthorsName = name => {
  const regex = /^(.+) (.+?)$/;
  const matches = name.match(regex);
  if (matches) {
    matches.shift();
    if (matches.length >= 2) {
      let first = matches.shift();
      let last = matches.shift();
      let middle = "";
      const middleMatches = first.match(regex);
      if (middleMatches) {
        middleMatches.shift();
        first = middleMatches.shift();
        middle = middleMatches.shift();
      }
      return {
        first: first.trim(),
        middle: middle.trim(),
        last: last.trim()
      };
    } else {
      return { last: matches[0].trim(), middle: "", first: "" };
    }
  } else {
    return { last: name.trim(), middle: "", first: "" };
  }
};

const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const splitName = name => {
  return name
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map(n => capitalizeFirstLetter(n));
};

const getAllContributors = contributors => {
  return (
    (contributors &&
      contributors.length &&
      contributors[0].name &&
      contributors[0].name.includes(";") &&
      contributors[0].name.split(";").map(c => c.trim())) ||
    []
  );
};

// parseMultipleAuthors is dependent on the book's metadata being set very specifically.
// If a book has multiple authors, the 'author' field in manage must be seperated with semi-colons.
// If the manage field does not have ;'s we revert back to the old way of doing citations.

// Always return a string for citation code to work with

// Multiple Author Citations Based off of:
// APA:   https://columbiacollege-ca.libguides.com/apa/booksandebooks
// HAR:   https://www.citethisforme.com/harvard-referencing
// MLA:   https://owl.purdue.edu/owl/research_and_citation/mla_style/mla_formatting_and_style_guide/mla_works_cited_page_books.html
// VAN:   https://guides.library.uq.edu.au/referencing/vancouver/books
// ABNT:  https://connect.ebsco.com/s/article/Citing-Articles-in-ABNT-Style?language=en_US
export const parseMultipleAuthors = (citationInfo, type) => {
  const allContributors = getAllContributors(citationInfo.contributors);
  let finalAuthorList = "";
  let primaryAuthor;
  let primaryLastName;
  const lastContributorIndex = allContributors.length - 1;
  if (allContributors.length) {
    switch (type) {
      case CITATION.abnt:
        allContributors.map((author, i) => {
          author = splitName(author);
          primaryLastName = author.pop();
          finalAuthorList += `${primaryLastName.toUpperCase()}`;
          if (author.length) {
            finalAuthorList += `, `;
            author.forEach((name, singleNameIndex) => {
              const firstInitial = name[0].toUpperCase();
              finalAuthorList += `${firstInitial}.`;
              if (
                singleNameIndex === author.length - 1 &&
                i !== lastContributorIndex
              ) {
                finalAuthorList += `; `;
              }
            });
          } else {
            // The author goes by a single name
            if (i !== lastContributorIndex) {
              finalAuthorList += `; `;
            }
          }
        });
        return finalAuthorList;
      case CITATION.apa:
        allContributors.forEach((name, i) => {
          const authorName = splitName(name);
          if (i === lastContributorIndex) {
            finalAuthorList += "& ";
          }
          finalAuthorList += authorName.pop();
          authorName.forEach(n => (finalAuthorList += ` ${n[0]}.`));
          if (i !== lastContributorIndex) {
            finalAuthorList += ", ";
          }
        });
        return finalAuthorList;
      case CITATION.har:
        allContributors.forEach((name, i) => {
          const authorName = splitName(name);
          if (i === lastContributorIndex) {
            finalAuthorList += `${i18n.t("base.and")} `;
          }

          // Run this check for authors who only have a single name
          if (i === lastContributorIndex && !authorName[1]) {
            finalAuthorList += `${authorName.pop()}`;
          } else {
            finalAuthorList += `${authorName.pop()}, `;
          }

          if (authorName && authorName[0]) {
            finalAuthorList += `${authorName[0][0]}.`;
            if (i !== lastContributorIndex) {
              finalAuthorList += ", ";
            }
          }
        });
        return finalAuthorList;
      case CITATION.van:
        allContributors.forEach((name, i) => {
          const maxNumberOfListedAuthors = 5;
          if (i <= maxNumberOfListedAuthors) {
            const authorName = splitName(name);
            finalAuthorList += authorName.pop();
            authorName.forEach(n => (finalAuthorList += ` ${n[0]}`));
            if (i !== lastContributorIndex) {
              if (i === maxNumberOfListedAuthors) {
                finalAuthorList += ` ${i18n.t("base.etal")}.`;
              } else {
                finalAuthorList += ", ";
              }
            }
          }
        });
        return finalAuthorList;
      case CITATION.mla:
        primaryAuthor = splitName(allContributors[0]);
        primaryLastName = primaryAuthor.pop();
        if (primaryAuthor.length) {
          primaryAuthor = " " + primaryAuthor.join(" ") + ",";
        } else {
          primaryAuthor = "";
        }
        if (allContributors.length >= 3) {
          return `${primaryLastName},${primaryAuthor} ${i18n.t("base.etal")}.`;
        } else {
          const remainingAuthors = allContributors;
          remainingAuthors.shift();
          return `${primaryLastName},${primaryAuthor} ${i18n.t(
            "base.and"
          )} ${remainingAuthors.join(" ")}`;
        }
      default:
        return "";
    }
  }
  return finalAuthorList;
};
